import { computed, reactive } from "vue";
import { defineStore } from "pinia";

interface XInvoiceState {
  /**
   * The invoice being paid through the currently open Paddle checkout, if any.
   */
  pendingInvoiceId: string | number | null

  /**
   * The invoice that was just paid through a completed Paddle checkout, if any.
   */
  paidInvoiceId: string | number | null
}

/**
 * Stands in for an invoice id while the Pay All checkout is open, since that
 * checkout settles every unpaid invoice rather than a single one.
 */
export const PAY_ALL_PAYMENT_ID = "pay-all";

/**
 * Bridges the global Paddle checkout events (`usePaddle`) with the components that
 * started an invoice payment, so they can release the row and reload the table.
 */
export const XInvoiceStore = defineStore("x-invoice", () => {
  //
  // Data
  const state = reactive<XInvoiceState>({
    pendingInvoiceId: null,
    paidInvoiceId: null,
  });

  //
  // Computed
  const pendingInvoiceId = computed<string | number | null>(() => state.pendingInvoiceId);
  const paidInvoiceId = computed<string | number | null>(() => state.paidInvoiceId);

  //
  // Methods

  /**
   * Marks the given invoice as awaiting payment.
   * Call it right before opening the Paddle checkout.
   */
  function beginInvoicePayment(invoiceId: string | number): void {
    state.pendingInvoiceId = invoiceId;
  }

  /**
   * Promotes the pending invoice to paid.
   * Called by the global Paddle event callback on "checkout.completed".
   */
  function completeInvoicePayment(): void {
    state.paidInvoiceId = state.pendingInvoiceId;
    state.pendingInvoiceId = null;
  }

  /**
   * Discards the pending payment.
   * Called when the Paddle checkout is closed manually by the user or fails.
   */
  function abortInvoicePayment(): void {
    state.pendingInvoiceId = null;
  }

  /**
   * Clears the paid invoice once the UI has finished reacting to it.
   */
  function resetInvoicePayment(): void {
    state.paidInvoiceId = null;
  }

  return {
    pendingInvoiceId,
    paidInvoiceId,
    beginInvoicePayment,
    completeInvoicePayment,
    abortInvoicePayment,
    resetInvoicePayment,
  };
});

export type InvoiceStore = ReturnType<typeof XInvoiceStore>

export default XInvoiceStore;
